import { FORBIDDEN_KEYS } from '../interpolation/forbiddenKeys';
import type {
  CatalogEventEntry,
  CatalogFamily,
  CatalogFieldRule,
  CatalogFieldType,
  CheckedBinding,
  EventBinding,
  BindingFieldSource,
} from './types';

/**
 * The bundled catalog's inferred event type leaves `authorable`, `fits`,
 * and field `type` optional. The composer still receives a checked entry.
 */
interface CatalogEventSource {
  readonly authorable?: boolean;
  readonly fits?: readonly string[];
  readonly acceptsCustomDimensions?: boolean;
  readonly family?: CatalogFamily;
  readonly fields?: Readonly<
    Record<
      string,
      {
        readonly type?: CatalogFieldType;
        readonly rules?: CatalogFieldRule;
      }
    >
  >;
}

interface ReadableEventCatalog {
  readonly events: Readonly<Record<string, CatalogEventSource>>;
  readonly reservedCustomDimensionKeys: readonly string[];
}

function knownFields(
  entry: CatalogEventEntry,
): Readonly<Record<string, unknown>> {
  return entry.fields ?? {};
}

function keepKnownFields(
  sources: Readonly<Record<string, BindingFieldSource>> | undefined,
  allowed: Readonly<Record<string, unknown>>,
): Readonly<Record<string, BindingFieldSource>> {
  if (!sources) return {};
  const kept: Record<string, BindingFieldSource> = {};
  for (const [path, source] of Object.entries(sources)) {
    if (FORBIDDEN_KEYS.has(path)) continue;
    if (Object.hasOwn(allowed, path)) kept[path] = source;
  }
  return kept;
}

function keepAuthorDimensions(
  sources: Readonly<Record<string, BindingFieldSource>> | undefined,
  reserved: ReadonlySet<string>,
): Readonly<Record<string, BindingFieldSource>> {
  if (!sources) return {};
  const kept: Record<string, BindingFieldSource> = {};
  for (const [key, source] of Object.entries(sources)) {
    if (FORBIDDEN_KEYS.has(key) || reserved.has(key)) continue;
    kept[key] = source;
  }
  return kept;
}

/**
 * Catalog check for one binding. A miss returns a reason and does not
 * throw. Unknown field paths and reserved custom-dimension keys are
 * removed from the result; they do not drop the binding.
 */
export function checkBinding(
  binding: EventBinding,
  momentClass: string,
  catalog: ReadableEventCatalog,
): CheckedBinding {
  if (!Object.hasOwn(catalog.events, binding.event)) {
    return { reason: 'unknown-event' };
  }
  const entry = catalog.events[binding.event];
  if (!entry || entry.authorable !== true) return { reason: 'not-authorable' };
  if (!entry.fits?.includes(momentClass))
    return { reason: 'moment-not-allowed' };

  const catalogEntry = entry as CatalogEventEntry;
  const fields = keepKnownFields(binding.fields, knownFields(catalogEntry));
  const customDimensions = catalogEntry.acceptsCustomDimensions
    ? keepAuthorDimensions(
        binding.customDimensions,
        new Set(catalog.reservedCustomDimensionKeys),
      )
    : {};

  return { entry: catalogEntry, fields, customDimensions };
}
